<template>
    <v-container id="startChatWrapper">

        <v-form 
        ref="form"
        id="startChat" 
        model="valid">

            <v-card flat v-if="isContributor && !isMockPublisherChat" class="contributorModeSelect">
                <v-card-title>Select Chat Mode</v-card-title>

                <v-card-subtitle><strong>NOTE:</strong> If you select 'Chat as Staff', escalation and qualifier features will not be active.</v-card-subtitle>

                <v-card-text class="modeSelectionWrapper">
                    <v-btn 
                    variant="elevated"
                    @click="handleStartChat"
                    color="primary">
                        Chat as Staff
                    </v-btn>

                    <v-btn 
                    v-if="publisherCookiePresent"
                    variant="elevated"
                    @click="handleSelectChatAsPublisher"
                    color="primary"
                    class="chatAsPublisherButton">Chat as Publisher ({{ publisherEmail }})
                    </v-btn>

                </v-card-text>
            </v-card>

            <v-stepper 
            v-else
            v-model="step"
            :items="stepItems"
            show-actions
            alt-labels
            flat>
                <template v-slot:[`item.1`]>
                    <v-card flat>

                        <v-card-title>{{ $t('START_CHAT_STEP_ONE_TITLE') }}</v-card-title>

                        <v-card-subtitle> {{ $t('START_CHAT_STEP_ONE_SUBTITLE') }}</v-card-subtitle>
                        
                        <v-card-text class="domainSelectionWrapper">    

                            <div class="selectLabel">{{ $t('SELECT_A_DOMAIN_PROMPT') }}</div>

                            <v-select 
                                v-model="formData.domainId"
                                :items="domainItems" 
                                item-title="domain"
                                item-value="domainId"
                                :label="$t('SELECT_A_DOMAIN_PLACEHOLDER')"
                                :required="isPublisher"
                                dense
                            ></v-select>

                        </v-card-text>

                    </v-card>

                </template>

                <template v-slot:[`item.2`]>
                    
                    <GetStarted></GetStarted>

                </template>

                <template v-slot:actions>

                    <div class="actionsWrapper">
                        <v-btn 
                        variant="text"
                        @click="handlePrevStep"
                        :disabled="prevStepDisabled">
                            {{ prevStepText }}
                        </v-btn>

                        <v-btn 
                        :variant="onFinalStep ? `elevated`: `tonal`"
                        :color="onFinalStep ? `primary` : ``"
                        @click="handleNextStep"
                        :disabled="nextStepDisabled">
                            {{ nextStepText }}
                        </v-btn>
                    </div>

                </template>

            </v-stepper>

        </v-form>

    </v-container>
</template>

<script>
    import GetStarted from "@/components/getstarted.vue";
    import { mapGetters, mapActions } from "vuex";

    export default {
        name: 'StartChat',
        components: {
            GetStarted,
        },
        data() {
            return {
                step: 1,
                stepItems: [ 
                    this.$t('START_CHAT_STEP_ONE'), // Choose a domain
                    this.$t('START_CHAT_STEP_TWO'), // Start chat
                ],
                formData: {
                    chatOriginId: this.$const.ORIGIN_ID__PROTEUS,
                    domainId: null,
                },
            };
        },
        async mounted() {
            if (this.isContractor || this.isContributor) return;

            if (!this.publisherDomainList.isLoaded) {
                this.incrementAppLoader();
                await this.getPublisherDomainList();
                this.decrementAppLoader();
            }
        },
        computed: {
            ...mapGetters([
                "user",
                "publisherDomainList", 
                "appLoading",
                "currentChat",
                "isMockPublisherChat",
            ]),
            isPublisher() {
                return this.user.userType === "publisher";
            },
            publisherCookiePresent() {
                return this.user.publisherId > 0;
            },
            publisherEmail() {
                return this.user.userEmail ? this.user.userEmail : "";
            },
            isContributor() {
                return this.user.userType === "contributor";
            },
            isContractor() {
                return this.user.userType === "contractor";
            },
            onFinalStep() {
                return this.step === this.stepItems.length;
            },
            prevStepDisabled() {
                return this.step === 1;
            },
            nextStepDisabled() {
                switch (this.step){
                    default:
                        return this.isPublisher && this.formData.domainId === null;
                }
            },
            nextStepText() {
                switch (this.step) {
                    case 2:
                        return this.$t('START_CHAT');
                    default:
                        return this.$t('NEXT_STEP');
                }
            },
            prevStepText() {
                return this.$t('PREVIOUS_STEP');
            },
            domainItems() {
                return this.publisherDomainList?.list || [];
            },
        },
        methods: {
            ...mapActions([
                "incrementAppLoader", 
                "decrementAppLoader", 
                "getPublisherDomainList",
                "startChat",
                "setIsMockPublisherChat",
            ]),
            async handleSelectChatAsPublisher() {
                this.incrementAppLoader();
                await this.getPublisherDomainList();
                this.decrementAppLoader();

                this.setIsMockPublisherChat(true);
            },
            handlePrevStep() {
                if (this.step > 1) {
                    this.step--;
                }
            },
            handleNextStep() {
                const lastStep = this.stepItems.length;

                switch (this.step) {
                    case lastStep:
                        this.handleStartChat();
                        break;
                    default:
                        this.step++;
                        break;
                }
            }, 
            async handleStartChat() {

                this.incrementAppLoader();
                this.clearChatIdCookie();

                const startChatInput = this.formData;
                if (startChatInput.domainId) {
                    startChatInput.domain = this.domainItems.find(d => d.domainId === startChatInput.domainId)?.domain;
                }

                const result = await this.startChat(startChatInput);
                if (result && this.currentChat.isLoaded) {
                    const chatId = this.currentChat.chatId;
                    this.setChatIdCookie(chatId);
                    this.$emit('chat-created');
                }

                this.decrementAppLoader();
            },
            clearChatIdCookie() {
                this.$cookies.remove('qabotChatId');
            },
            setChatIdCookie(chatId) {
                this.$cookies.set('qabotChatId', chatId, {
                    expires: '1d',
                    secure: true
                });
            },
        },
    }

</script>

<style lang="scss">
@import "./startchat.scss";
</style>